import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { formatDate } from '../format';
import { StatusBadge } from '../components/StatusBadge';
import { GuideLink } from '../evals/GuideLink';

// Neo's input gate (TOM-303, ADR 0300): the turns where Jev, the classifier
// that decides, and the regex baseline reached different outcomes. A row where
// Jev allowed what the regex refused is a regex false positive, or a miss by
// Jev; a row where Jev refused what the regex allowed is an attack the regex
// missed, or a Jev false positive. Each row holds the owner's message, so this
// is the refusals list's content boundary: an access reason first (the server
// logs it fail-closed), and the text masked by default. `?id=<row id>` narrows
// the list to one row, whatever its age.

type Outcome = 'allow' | 'safety' | 'security';

interface GateRow {
  id: string;
  user_id: string;
  short_id: string;
  conversation_id: string | null;
  refusal_id: string | null;
  decided_by: 'typesafe' | 'openrouter';
  jev_outcome: Outcome;
  regex_outcome: Outcome;
  jev_scores: Record<string, number>;
  gate_version: string;
  query_text: string;
  created_at: string;
}

interface GateList {
  disagreements: GateRow[];
  nextCursor: string | null;
}

const WINDOWS = [7, 30, 90];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The gate's questions in its order (src/core/neo/input-gate.ts), with short labels.
const QUESTIONS: ReadonlyArray<[string, string]> = [
  ['abusive', 'abuse'],
  ['overrides_instructions', 'override'],
  ['extracts_secrets', 'secrets'],
  ['harmful', 'harm'],
  ['unsafe_maritime', 'unsafe'],
];

const ROUTE_LABEL: Record<GateRow['decided_by'], string> = {
  typesafe: 'TypeSafe',
  openrouter: 'OpenRouter',
};

function gateListUrl(
  days: number,
  accessReason: string,
  onlyId: string | null,
  cursor?: string
): string {
  return `/v1/admin/neo/gate-disagreements?days=${days}&reason=${encodeURIComponent(accessReason)}${
    onlyId !== null ? `&id=${onlyId}` : ''
  }${cursor !== undefined ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
}

function Scores({ scores, version }: { scores: Record<string, number>; version: string }) {
  const shown = QUESTIONS.filter(([key]) => scores[key] !== undefined);
  const top = shown.reduce<string | null>(
    (best, [key]) => (best === null || scores[key]! > scores[best]! ? key : best),
    null
  );
  return (
    <div className="neo-admin-gate-scores" title={version}>
      {shown.map(([key, label]) => {
        const cls = key === top ? 'neo-admin-gate-top' : undefined;
        return (
          <Fragment key={key}>
            <span className={cls}>{label}</span>
            <span className={cls}>{scores[key]!.toFixed(2)}</span>
          </Fragment>
        );
      })}
    </div>
  );
}

function OutcomeBadge({ outcome }: { outcome: Outcome }) {
  return <StatusBadge status={outcome === 'allow' ? 'published' : 'rejected'} label={outcome} />;
}

export function AdminNeoGate() {
  const [search, setSearch] = useSearchParams();
  const rawId = search.get('id');
  const onlyId = rawId !== null && UUID.test(rawId) ? rawId.toLowerCase() : null;
  const [reason, setReason] = useState('');
  const [accessReason, setAccessReason] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [data, setData] = useState<GateList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  useEffect(() => {
    if (accessReason === null) return;
    let ignore = false;
    setData(null);
    setError(null);
    adminApi<GateList>(gateListUrl(days, accessReason, onlyId))
      .then((res) => {
        if (!ignore) setData(res);
      })
      .catch((err) => {
        if (ignore) return;
        const e = err as { body?: { message?: string } };
        setError(e.body?.message ?? 'Failed to load the gate disagreements.');
      });
    return () => {
      ignore = true;
    };
  }, [days, accessReason, onlyId]);

  function loadOlder() {
    if (accessReason === null || data === null || data.nextCursor === null) return;
    setLoadingOlder(true);
    adminApi<GateList>(gateListUrl(days, accessReason, onlyId, data.nextCursor))
      .then((res) =>
        setData((prev) =>
          prev === null
            ? res
            : {
                disagreements: [...prev.disagreements, ...res.disagreements],
                nextCursor: res.nextCursor,
              }
        )
      )
      .catch((err) => {
        const e = err as { body?: { message?: string } };
        setError(e.body?.message ?? 'Failed to load older rows.');
      })
      .finally(() => setLoadingOlder(false));
  }

  function open(e: FormEvent) {
    e.preventDefault();
    if (reason.trim().length < 3) return;
    setAccessReason(reason.trim());
  }

  if (accessReason === null) {
    return (
      <section className="admin-section">
        <header className="admin-section-header">
          <h1>Neo input gate</h1>
          <GuideLink section="fix-find" />
        </header>
        <p className="admin-hint">
          Messages where Jev, which decides, and the regex baseline disagreed: what Jev let through
          that the regex would have refused, and what Jev refused that the regex would have let
          through.
        </p>
        {onlyId !== null ? (
          <p className="admin-hint" data-testid="neo-gate-one">
            You are opening one row, <code>{onlyId.slice(0, 8)}</code>.
          </p>
        ) : null}
        <div className="admin-auth-banner">
          This list shows users&apos; messages (private content). Your access is logged with your
          account and your stated reason.
        </div>
        <form className="neo-admin-reason" onSubmit={open}>
          <label htmlFor="neo-gate-reason">Reason for viewing</label>
          <input
            id="neo-gate-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. reviewing the gate's disagreements this week"
            minLength={3}
            maxLength={200}
            required
          />
          <button type="submit" className="admin-button" disabled={reason.trim().length < 3}>
            View disagreements
          </button>
        </form>
      </section>
    );
  }

  const rows = data?.disagreements ?? [];
  const jevAllowed = rows.filter((r) => r.jev_outcome === 'allow').length;
  const jevRefused = rows.filter((r) => r.regex_outcome === 'allow').length;
  const otherReason = rows.length - jevAllowed - jevRefused;

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Neo input gate</h1>
        <span className="admin-count">
          {onlyId !== null ? (
            <button
              type="button"
              className="admin-link-button"
              onClick={() => setSearch(new URLSearchParams())}
            >
              Show every row
            </button>
          ) : (
            <span className="admin-seg">
              {WINDOWS.map((w) => (
                <button
                  key={w}
                  type="button"
                  className="admin-link-button"
                  disabled={w === days}
                  onClick={() => setDays(w)}
                >
                  {w}d
                </button>
              ))}
            </span>
          )}
          <button type="button" className="admin-link-button" onClick={() => setReveal((v) => !v)}>
            {reveal ? 'Mask text' : 'Reveal text'}
          </button>
        </span>
        <GuideLink section="fix-find" />
      </header>
      <div className="admin-auth-banner">Viewing users&apos; messages — access logged.</div>

      {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
      {data === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {data !== null ? (
        rows.length === 0 ? (
          <p className="admin-empty">
            {onlyId !== null ? 'No row with this id.' : 'No disagreements in this window.'}
          </p>
        ) : (
          <>
            <p className="admin-hint" data-testid="neo-gate-counts">
              {rows.length} {rows.length === 1 ? 'row' : 'rows'}: Jev allowed what the regex refused{' '}
              {jevAllowed}, Jev refused what the regex allowed {jevRefused}, both refused for
              different reasons {otherReason}.
            </p>
            <div className="scroll-x">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Verdicts</th>
                    <th>Jev scores</th>
                    <th>What the user asked</th>
                    <th>Links</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`neo-gate-${r.id}`}>
                      <td>
                        <div className="neo-admin-gate-stack">
                          <span>{formatDate(r.created_at)}</span>
                          <Link to={`/admin/users/${r.user_id}`}>
                            <code>{r.short_id}</code>
                          </Link>
                        </div>
                      </td>
                      <td>
                        <div className="neo-admin-gate-verdicts">
                          <span>Jev</span>
                          <OutcomeBadge outcome={r.jev_outcome} />
                          <span>Regex</span>
                          <OutcomeBadge outcome={r.regex_outcome} />
                          <span />
                          <span>{ROUTE_LABEL[r.decided_by]}</span>
                        </div>
                      </td>
                      <td>
                        <Scores scores={r.jev_scores} version={r.gate_version} />
                      </td>
                      <td className={reveal ? '' : 'neo-admin-masked'}>{r.query_text}</td>
                      <td>
                        <div className="neo-admin-gate-links">
                          {r.refusal_id !== null ? (
                            <Link to={`/admin/neo/refusals?id=${r.refusal_id}`}>Refusal →</Link>
                          ) : null}
                          {r.conversation_id !== null ? (
                            <Link to={`/admin/neo/sessions/${r.conversation_id}`}>Session →</Link>
                          ) : null}
                          {r.refusal_id === null && r.conversation_id === null ? '—' : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.nextCursor !== null ? (
              <button
                type="button"
                className="admin-link-button"
                disabled={loadingOlder}
                onClick={loadOlder}
              >
                {loadingOlder ? 'Loading…' : 'Load older'}
              </button>
            ) : null}
          </>
        )
      ) : null}
    </section>
  );
}
